/**
 * ENG-8465 — decide whether a minute of measured occupancy was WORK.
 *
 * ## The defect this exists to fix
 *
 * `pane-occupancy-sampler.ts` measures Claude Code occupancy from `pane.log`'s
 * mtime, because that is the only host-visible signal that keeps ticking while
 * an agent is blocked on a tool it invoked. That density is the feature — it is
 * what lets a 45-second `pnpm test` bill as occupied — but the substrate is
 * TOKEN-BLIND: a boot banner, a `Ready.`, an `Auto-updating…` spinner frame and
 * a real model turn all advance the same mtime by the same amount.
 *
 * Measured on `ledger` (`911e951b`, prod, 7 days): 221 billed minutes, **86% of
 * them in runs of <= 2 minutes and not one run longer than 4 minutes**, against
 * zero completed tasks in four weeks. That distribution is the fingerprint of
 * isolated pane writes, not of work.
 *
 * Note what does NOT fix it. Shrinking the sampler's post-idle credit does
 * nothing: `MIN_BILLABLE_MS` is 100ms and `BILL_WHOLE_MINUTES` rounds any
 * credited bucket up to a full 60s, so an instantaneous repaint bills one or two
 * whole minutes however narrow the credit is. The credit width only decides
 * whether it costs one minute or two. The lever is not how much to credit — it
 * is WHETHER to credit at all.
 *
 * ## The discriminator
 *
 * An assistant turn that did work emits hundreds to thousands of output tokens.
 * A boot turn emits single digits (`ledger`'s showed 2). A spinner frame emits
 * none, because it never reaches the transcript at all. So: a minute counts as
 * work only if a qualifying assistant turn sits near it.
 *
 * That is a discriminator with no enumeration in it. The sampler's existing
 * probe debounce had to name the synthetic probe; boot, update and rollover were
 * never named and so counted by default. This rule does not care what wrote to
 * the pane — only whether the model produced anything.
 *
 * ## Why a NEIGHBOURHOOD rather than "a turn inside this minute"
 *
 * The transcript is token-aware but temporally SPARSE, and that is the trap that
 * sinks the obvious design. `wedge-detection.ts` records `transcriptAge=113s`
 * while `paneAge=0s` on a working agent, and 202s mid-build — a single long tool
 * call writes a `tool_use` entry and then NOTHING until the `tool_result`
 * returns, which is why that module's staleness threshold had to be widened to
 * 900s. Requiring a turn INSIDE each minute would therefore un-bill exactly the
 * long-tool-call occupancy the pane substrate was chosen to capture, converting
 * a bounded false positive into a large and invisible false negative.
 *
 * So a bucket qualifies when a qualifying turn falls within
 * {@link DEFAULT_NEIGHBOURHOOD_MS} of it, while an isolated repaint with no turn
 * near it on either side does not qualify at all.
 *
 * That reach is BOUNDED, and an earlier revision of this paragraph overstated
 * it: it claimed the turn that LAUNCHED a long tool call "keeps the whole wait
 * qualified". It does not. {@link isBucketQualified} tests
 * `[bucketStart - n, bucketStart + 60s + n]`, so one turn covers six
 * bucket-starts and no more — about three minutes of wait, not an arbitrary
 * one. Past that the launching turn is out of reach and the middle of a long
 * call drops. ENG-9178 closes that gap with tool-call BRACKETS (below); this
 * paragraph is left explicit about the limit because the wrong version of it
 * was load-bearing in two subsequent design discussions.
 *
 * ## The shape trap (ENG-8194)
 *
 * A turn refused for the usage cap IS an `assistant` record and DOES carry a
 * `usage` block — all zeros, `model: "<synthetic>"`, `isApiErrorMessage: true`.
 * `rate-limit-classifier.ts` documents an earlier cut of that module which asked
 * "has any assistant turn happened recently?" and so counted every FAILED turn on
 * a capped agent as evidence of serving. The same shape would let a capped agent
 * bill continuously here. Synthetic and API-error records are therefore rejected
 * explicitly, before the token floor is applied — keyed on the positive signal,
 * not on the absence of one.
 *
 * ## What this deliberately does NOT do: the probe debounce
 *
 * A synthetic or question probe works by injecting a real direct-chat turn. That
 * turn emits real output tokens and clears any floor set here — so this gate does
 * NOT, and cannot, subsume the probe debounce. Probe episodes live in the
 * `agent_synthetic_probes` / `agent_question_probes` TABLES, not in the
 * transcript, so a pure JSONL function has nothing to test against.
 *
 * The debounce stays where it already is: the `NOT EXISTS` blocks in
 * `record_agent_activity_buckets()` and `sample_agent_activity()`
 * (`20260726000005_reported_busy_buckets.sql:164,172,260,269`), both untouched by
 * this module. ENG-7549 measured an idle agent's hourly question-probe reply
 * billing as five busy minutes, which is what those blocks exist to prevent.
 *
 * Stated explicitly because the tempting inference is the opposite one: an early
 * cut of ENG-8465 proposed REMOVING the probe special case on the theory that a
 * token floor made it redundant. It does not. A floor and an episode debounce are
 * orthogonal — the floor asks "did the model produce anything", the debounce asks
 * "did WE cause it". Deleting either one re-opens a defect the other never covered.
 *
 * Pure by construction, matching this module's convention: a function of JSONL
 * text and a time window, with no `node:fs` and no path handling, so it stays
 * browser-safe and each consumer keeps its own reader.
 */

/**
 * Output-token floor for a turn to count as work.
 *
 * Empirical, not guessed. On a real 32 MB transcript (117h, 4901 turns carrying
 * output tokens) only **13 turns — 0.27% — emitted fewer than 10 output
 * tokens**, so the floor separates cleanly: essentially every real turn clears
 * it by two orders of magnitude, while `ledger`'s boot turn reported 2.
 *
 * Set at 25 rather than 10 to leave headroom above the boot shape without
 * approaching the smallest real answers. A one-line reply ("Done.", "Yes — see
 * line 40.") still runs to tens of tokens.
 *
 * Deliberately NOT tuned until one agent reads zero. `ledger` is a fixture, and
 * a floor fitted to it would be overfitted to it; this value is set from the
 * distribution of real turns and then checked against `ledger`, not the reverse.
 */
export const DEFAULT_MIN_OUTPUT_TOKENS = 25;

/**
 * How far from a bucket a qualifying turn may sit and still qualify it.
 *
 * Matches `IDLE_GAP_MS` in the sampler, which is the longest silent stretch that
 * module already treats as continuous occupancy. Using the same value keeps one
 * definition of "still the same episode" rather than introducing a second,
 * differently-tuned one — if the sampler is willing to accrue across a 120s pane
 * silence, this must be willing to qualify across it, or the two rules disagree
 * about the same minute and the gate silently un-bills what the sampler measured.
 */
export const DEFAULT_NEIGHBOURHOOD_MS = 120_000;

/** Wall-clock bucket width. Matches `agent_activity_samples.bucket_seconds`. */
const BUCKET_MS = 60_000;

export interface QualifyingTurnOptions {
  /** Minimum `message.usage.output_tokens`. Defaults to {@link DEFAULT_MIN_OUTPUT_TOKENS}. */
  minOutputTokens?: number;
  /** Ignore turns stamped before this epoch-ms bound. Defaults to unbounded. */
  startMs?: number;
  /** Ignore turns stamped after this epoch-ms bound. Defaults to unbounded. */
  endMs?: number;
}

/**
 * True when an `assistant` record is a synthetic / API-error entry rather than a
 * produced turn. See the shape-trap note above — these carry a `usage` block and
 * would otherwise clear any floor set from the boot shape.
 *
 * Checked positively on every marker Claude Code has been observed to use
 * (`turn-failure-classifier.ts` enumerates the same set), so a record needs only
 * ONE of them to be rejected. Rejecting too much here costs an under-count on a
 * failed turn, which is the safe direction; accepting too much bills a capped
 * agent for doing nothing.
 */
function isSyntheticOrErrorRecord(
  record: Record<string, unknown>,
  msg: Record<string, unknown>,
): boolean {
  if (msg.model === '<synthetic>') return true;
  if (record.isApiErrorMessage === true) return true;
  if (typeof record.error === 'string' && record.error) return true;
  if (typeof record.apiErrorStatus === 'number') return true;
  return false;
}

/**
 * Resolve the caller's floor, falling back to the default on anything that is
 * not a usable threshold.
 *
 * This matters more than a normal input check because of the direction the bad
 * values fail in. `outputTokens < NaN` is ALWAYS false, so a `NaN` floor —
 * trivially produced by `Number(undefined)` or a parsed env var — qualifies every
 * turn ever written, silently converting the gate into a no-op on a meter that
 * bills customers. A negative floor does the same thing deterministically.
 *
 * So an unusable floor falls back to {@link DEFAULT_MIN_OUTPUT_TOKENS} rather
 * than being clamped to 0: clamping would preserve the "everything qualifies"
 * behaviour that makes the mistake invisible, whereas the default keeps the gate
 * doing its job while the caller's bug surfaces somewhere it can be seen.
 */
function resolveFloor(value: unknown): number {
  if (typeof value !== 'number' || !Number.isFinite(value) || value < 0) {
    return DEFAULT_MIN_OUTPUT_TOKENS;
  }
  return value;
}

function nonNegInt(value: unknown): number {
  return typeof value === 'number' && Number.isFinite(value) && value > 0
    ? Math.floor(value)
    : 0;
}

/**
 * Epoch-ms timestamps of every assistant turn in `jsonl` that produced at least
 * `minOutputTokens` output tokens, ascending and de-duplicated.
 *
 * Dedupe is on `message.id`, keeping the LAST usage seen for an id — a streamed
 * turn emits the same id repeatedly with successive usage snapshots, and the
 * final one is authoritative. This mirrors `parseTranscriptUsage` exactly; a
 * turn that streams up past the floor must not qualify twice, and one whose
 * final snapshot lands below the floor must not qualify on an intermediate one.
 *
 * Defensive by design, like every reader of this undocumented format: malformed
 * lines, missing fields and unknown record types are skipped rather than thrown.
 * A parse failure degrades to "fewer qualifying turns", which under-bills. That
 * is the correct direction to fail on a meter — but it is also silent, which is
 * why the caller is expected to emit a divergence signal when a transcript
 * yields no qualifying turns while the pane is hot.
 */
export function extractQualifyingTurns(
  jsonl: string,
  options: QualifyingTurnOptions = {},
): number[] {
  const floor = resolveFloor(options.minOutputTokens);
  const startMs = options.startMs ?? Number.NEGATIVE_INFINITY;
  const endMs = options.endMs ?? Number.POSITIVE_INFINITY;

  // id -> { tsMs, outputTokens } for the LAST snapshot seen of that message.
  const byId = new Map<string, { tsMs: number; outputTokens: number }>();
  let syntheticCounter = 0;

  for (const line of jsonl.split('\n')) {
    const trimmed = line.trim();
    if (!trimmed) continue;

    let obj: unknown;
    try {
      obj = JSON.parse(trimmed);
    } catch {
      continue;
    }
    if (typeof obj !== 'object' || obj === null) continue;
    const record = obj as Record<string, unknown>;
    if (record.type !== 'assistant') continue;

    const message = record.message;
    if (typeof message !== 'object' || message === null) continue;
    const msg = message as Record<string, unknown>;

    if (isSyntheticOrErrorRecord(record, msg)) continue;

    const usage = msg.usage;
    if (typeof usage !== 'object' || usage === null) continue;

    const ts = record.timestamp;
    if (typeof ts !== 'string' || !ts) continue;
    const tsMs = new Date(ts).getTime();
    if (!Number.isFinite(tsMs)) continue;

    const id =
      typeof msg.id === 'string' && msg.id ? msg.id : `__noid_${syntheticCounter++}`;
    byId.set(id, {
      tsMs,
      outputTokens: nonNegInt((usage as Record<string, unknown>).output_tokens),
    });
  }

  const out: number[] = [];
  for (const entry of byId.values()) {
    if (entry.outputTokens < floor) continue;
    if (entry.tsMs < startMs || entry.tsMs > endMs) continue;
    out.push(entry.tsMs);
  }
  out.sort((a, b) => a - b);
  return out;
}

/**
 * True when `bucketStartMs` (the start of a wall-clock minute) has a qualifying
 * turn within `neighbourhoodMs` of it, on either side.
 *
 * The bucket's own span counts as inside the neighbourhood, so the test is
 * against `[bucketStart - n, bucketEnd + n]`. Symmetric deliberately: a turn
 * just BEFORE the minute is the one that launched a tool call running through
 * it, and a turn just AFTER is that call returning. Both are evidence the minute
 * was occupied by work; only a minute with neither is an unattributed repaint.
 *
 * `turnTimes` must be ascending — as returned by {@link extractQualifyingTurns}.
 * A binary search would be faster, but the caller holds at most a few minutes of
 * turns per drain, so the linear scan is not worth the off-by-one risk.
 */
export function isBucketQualified(
  bucketStartMs: number,
  turnTimes: readonly number[],
  neighbourhoodMs: number = DEFAULT_NEIGHBOURHOOD_MS,
): boolean {
  const from = bucketStartMs - neighbourhoodMs;
  const to = bucketStartMs + BUCKET_MS + neighbourhoodMs;
  for (const t of turnTimes) {
    if (t > to) return false; // ascending — nothing further can match
    if (t >= from) return true;
  }
  return false;
}

/* ===========================================================================
 * ENG-9178 — tool-call brackets: the second way a minute can be evidenced.
 *
 * ## Why the turn neighbourhood is not enough
 *
 * {@link isBucketQualified} tests `[bucketStart - n, bucketStart + 60s + n]`,
 * so with the default 120s a single turn at T qualifies bucket-starts
 * `T-3min .. T+2min` — SIX buckets, and not one more. That is ample for the
 * case the docblock above describes (a turn, a short tool call, a reply) and
 * silently insufficient for the case it CLAIMS to cover.
 *
 * A 24-minute tool call launched by a turn at 0:00 and returning at 24:00
 * writes a `tool_use` block, then nothing at all until the `tool_result`
 * arrives. Buckets 3..20 have no qualifying turn within reach on either side,
 * so under `enforce` they are dropped — in the middle of a tool call the
 * transcript itself proves was running. That is the direction that UNDER-bills,
 * and it is invisible: `agent_activity_samples` has no provenance column, so
 * after the fact there is no way to ask which minutes were dropped or why.
 *
 * ## The bracket
 *
 * A `tool_use` block paired with the `tool_result` block carrying the same
 * `tool_use_id` is a closed interval during which the agent was, by the
 * transcript's own account, waiting on a tool it invoked. A bucket overlapping
 * that interval is evidenced work even with no turn anywhere near it.
 *
 * This is ADDITIVE. It never removes a qualification the turn neighbourhood
 * granted, and it never decides HOW LONG anything ran — the pane sampler still
 * owns duration, and a bracket with no measured occupancy under it accrues
 * exactly nothing. Intersect, do not substitute.
 *
 * ## Why the parse is split from the pairing
 *
 * The caller reads a BOUNDED TAIL of each transcript, so a bracket routinely
 * straddles reads: the `tool_use` is in one drain's tail and the `tool_result`
 * lands in a later one, by which time the `tool_use` may have scrolled out
 * entirely. Pairing therefore needs state the caller carries between reads,
 * which a single `(jsonl) => brackets` function cannot express.
 *
 * So {@link extractToolCallEvents} is a pure parse of one text, and
 * {@link pairToolCallBrackets} is a pure fold of those events over the caller's
 * carried open set. Both stay free of `node:fs`, matching this module's
 * convention, and the cross-drain behaviour is unit-testable by calling the
 * fold twice — no filesystem, no clock.
 * ======================================================================== */

/** One end of a tool call, as observed in a transcript. */
export interface ToolCallEvent {
  /** The `tool_use_id` both ends share. */
  id: string;
  /** `use` opens the bracket; `result` closes it. */
  kind: 'use' | 'result';
  /** Epoch-ms from the record's ROOT `timestamp`. */
  atMs: number;
}

/** A tool call the caller is still waiting to see close. */
export interface OpenToolCall {
  id: string;
  /** Epoch-ms the `tool_use` was written. */
  startMs: number;
}

/** A tool call observed from `tool_use` through to its `tool_result`. */
export interface ToolCallBracket {
  id: string;
  startMs: number;
  /** Epoch-ms of the `tool_result`. Always >= `startMs`. */
  endMs: number;
}

export interface ToolCallEventOptions {
  /** Ignore events stamped before this epoch-ms bound. Defaults to unbounded. */
  startMs?: number;
  /** Ignore events stamped after this epoch-ms bound. Defaults to unbounded. */
  endMs?: number;
}

/**
 * Every `tool_use` / `tool_result` block in `jsonl`, in FILE ORDER.
 *
 * Shape notes, each of which is a way a naive reader gets this wrong:
 *
 *   - A `tool_use` block lives in the `content` ARRAY of an `assistant` record;
 *     its `tool_result` lives in the `content` array of a later `user` record.
 *     Neither carries its own timestamp — both are stamped from the ROOT
 *     `timestamp` of the record that contains them.
 *   - ONE assistant record can carry SEVERAL `tool_use` blocks (parallel tool
 *     calls), and one user record can carry several `tool_result` blocks. Every
 *     block is emitted, so a record contributing five events is normal.
 *   - `message.content` is a plain STRING on many records. Those carry no
 *     blocks and are skipped, not treated as malformed.
 *   - The record `type` is NOT checked. A `tool_use` block is a `tool_use`
 *     block wherever it appears, and pinning the pairing to a `type` the format
 *     is free to change would fail closed in the under-billing direction — the
 *     one this exists to fix.
 *
 * Defensive like every reader of this undocumented format: malformed lines,
 * missing fields and unknown record types are skipped rather than thrown. The
 * failure direction is "fewer brackets", i.e. fewer qualified minutes, i.e.
 * under-billing — correct for a meter, and silent, which is why the caller is
 * expected to count bracket outcomes separately rather than infer them.
 */
export function extractToolCallEvents(
  jsonl: string,
  options: ToolCallEventOptions = {},
): ToolCallEvent[] {
  const startMs = options.startMs ?? Number.NEGATIVE_INFINITY;
  const endMs = options.endMs ?? Number.POSITIVE_INFINITY;
  const out: ToolCallEvent[] = [];

  for (const line of jsonl.split('\n')) {
    const trimmed = line.trim();
    if (!trimmed) continue;

    let obj: unknown;
    try {
      obj = JSON.parse(trimmed);
    } catch {
      continue;
    }
    if (typeof obj !== 'object' || obj === null) continue;
    const record = obj as Record<string, unknown>;

    const ts = record.timestamp;
    if (typeof ts !== 'string' || !ts) continue;
    const atMs = new Date(ts).getTime();
    if (!Number.isFinite(atMs)) continue;
    if (atMs < startMs || atMs > endMs) continue;

    const message = record.message;
    if (typeof message !== 'object' || message === null) continue;
    const content = (message as { content?: unknown }).content;
    if (!Array.isArray(content)) continue;

    for (const raw of content) {
      if (!raw || typeof raw !== 'object' || Array.isArray(raw)) continue;
      const block = raw as Record<string, unknown>;

      if (block.type === 'tool_use') {
        const id = typeof block.id === 'string' ? block.id : '';
        if (id) out.push({ id, kind: 'use', atMs });
        continue;
      }
      if (block.type === 'tool_result') {
        const id = typeof block.tool_use_id === 'string' ? block.tool_use_id : '';
        if (id) out.push({ id, kind: 'result', atMs });
      }
    }
  }

  // FILE ORDER, deliberately not sorted by timestamp. The transcript is
  // append-only, so the order records were written is the CAUSAL order: a
  // `tool_result` physically after a `tool_use` is that call's result, whatever
  // the two timestamps say. Sorting by `atMs` would reorder a clock-stepped
  // result BEFORE its own use, and `pairToolCallBrackets` would then discard it
  // as an orphan — losing the whole bracket, silently, in the under-billing
  // direction. Callers that want chronological order can sort the brackets.
  return out;
}

/** What one fold of {@link pairToolCallBrackets} learned. */
export interface PairedToolCalls {
  /** Calls seen to complete, ascending by `startMs`. */
  brackets: ToolCallBracket[];
  /** Calls opened but not yet closed, ascending by `startMs`. Carry these forward. */
  open: OpenToolCall[];
}

/**
 * Fold tool-call events over the caller's carried open set.
 *
 * `carriedOpen` is what a previous fold returned as {@link PairedToolCalls.open}
 * — the calls whose `tool_use` has been seen and whose `tool_result` has not.
 * Passing it back in is what lets a bracket span reads: the `tool_use` can have
 * scrolled out of the tail entirely and the pairing still completes.
 *
 * Three rules that are each load-bearing:
 *
 *   - A `result` with no matching `use`, in either the events or the carried
 *     set, is DISCARDED — never turned into a zero-length or open-ended
 *     bracket. Its `tool_use` is before everything we hold, so its true start
 *     is unknown, and inventing one would credit minutes on no evidence.
 *   - A repeated `use` for an id already open keeps the EARLIER start. The tail
 *     is re-read every drain, so the same `tool_use` is observed many times;
 *     taking the later one would walk the bracket's start forward and shrink it
 *     by exactly the buckets in dispute.
 *   - A `result` stamped BEFORE its `use` (clock step, out-of-order write)
 *     yields a bracket clamped to `endMs = startMs` rather than a reversed
 *     interval. A reversed interval silently matches nothing, which reads
 *     identically to "no tool call ran".
 *
 * Pure: no clock, no filesystem. Expiry of a long-open call is the caller's
 * decision, because only the caller knows what "now" is and what bound it wants.
 */
export function pairToolCallBrackets(
  events: readonly ToolCallEvent[],
  carriedOpen: readonly OpenToolCall[] = [],
): PairedToolCalls {
  const open = new Map<string, number>();
  for (const c of carriedOpen) {
    const prev = open.get(c.id);
    if (prev === undefined || c.startMs < prev) open.set(c.id, c.startMs);
  }

  // Folded in the order given — i.e. file order (see extractToolCallEvents).
  // Re-sorting here would reintroduce exactly the reordering that function
  // avoids.
  const brackets: ToolCallBracket[] = [];
  for (const ev of events) {
    if (ev.kind === 'use') {
      const prev = open.get(ev.id);
      // Keep the EARLIEST start seen for an id — the tail is re-read every
      // drain, so a later sighting of the same `tool_use` must not move it.
      if (prev === undefined || ev.atMs < prev) open.set(ev.id, ev.atMs);
      continue;
    }
    const startMs = open.get(ev.id);
    if (startMs === undefined) continue; // result with no known start — discard
    open.delete(ev.id);
    brackets.push({ id: ev.id, startMs, endMs: Math.max(startMs, ev.atMs) });
  }

  brackets.sort((a, b) => a.startMs - b.startMs);
  const stillOpen = [...open.entries()]
    .map(([id, startMs]) => ({ id, startMs }))
    .sort((a, b) => a.startMs - b.startMs);
  return { brackets, open: stillOpen };
}

/**
 * True when the wall-clock minute starting at `bucketStartMs` OVERLAPS a closed
 * tool-call bracket.
 *
 * Overlap, not containment. A bucket is a minute; a bracket is an arbitrary
 * span. Requiring containment would drop the first and last minute of every
 * tool call — the two minutes most likely to also hold the launching turn, so
 * the bug would hide behind the neighbourhood rule and only surface on calls
 * long enough for the middle to be exposed.
 *
 * The interval is CLOSED at both ends: a bracket that opens exactly at the
 * bucket's final instant, or closes exactly at its first, still counts. On a
 * meter, the tie goes to the measurement that already proved the agent was
 * occupied.
 */
export function isBucketInBracket(
  bucketStartMs: number,
  brackets: readonly ToolCallBracket[],
): boolean {
  const bucketEndMs = bucketStartMs + BUCKET_MS;
  for (const b of brackets) {
    if (b.startMs > bucketEndMs) continue;
    if (b.endMs >= bucketStartMs) return true;
  }
  return false;
}

/**
 * True when the minute starting at `bucketStartMs` sits at or after the start of
 * a call that is still OPEN — i.e. the transcript shows a tool invoked and not
 * yet returned, covering this minute.
 *
 * Deliberately NOT a qualification. An open call has no end, so crediting it
 * would bill a wedged MCP server, a killed subprocess or a rotated transcript
 * forever — re-opening the exact defect the qualifier exists to close, from the
 * other side. The caller uses this to DEFER a bucket: hold it, judge it again
 * once the call closes, and drop it if it never does.
 */
export function isBucketInOpenCall(
  bucketStartMs: number,
  open: readonly OpenToolCall[],
): boolean {
  const bucketEndMs = bucketStartMs + BUCKET_MS;
  for (const c of open) {
    if (c.startMs <= bucketEndMs) return true;
  }
  return false;
}
